import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { StyledPaper, StyledTable, StyledTableRow, StyledTableCell } from '../../../uiComponents/MuiTable/StyledTable.js';
import MuiTablePagination from '../../../uiComponents/MuiTable/MuiTablePagination';
import MuiTableHeader from '../../../uiComponents/MuiTable/MuiTableHeader.js';
import { stableSort, getComparator, handleRequestSort, handleChangePage, handleChangeRowsPerPage } from '../../../uiComponents/MuiTable/MuiHelper';
import {
  TableBody,
  TableContainer,
  Box,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import { RemoveCircleOutline } from '@mui/icons-material';

// Preserves the table's pagination/sort so it can be restored when the user
// navigates to variant details and clicks the browser back button. The storage
// key is configurable so this table can be reused in separate flows (e.g. the
// genotype search) without their pagination state colliding.
const DEFAULT_TABLE_STATE_KEY = 'exploreVariantSearchTable';

const getSavedTableState = (tableStateKey) => {
  try {
    return JSON.parse(sessionStorage.getItem(tableStateKey)) || {};
  } catch {
    return {};
  }
};

const SearchVariantsResultsTable = ({ data, breadCrumbs, noResultsMessage, onAddVariant, onRemoveVariant, addedVariantNames, tableStateKey }) => {
  const savedTableState = getSavedTableState(tableStateKey);
  const [order, setOrder] = useState(savedTableState.order || 'asc'); //default sort direction
  const [orderBy, setOrderBy] = useState(savedTableState.orderBy || 'hgvsGrch38Name'); //default sort column for table
  const [page, setPage] = useState(Number.isInteger(savedTableState.page) ? savedTableState.page : 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    Number.isInteger(savedTableState.rowsPerPage) ? savedTableState.rowsPerPage : 10
  );

  useEffect(() => {
    // Persist pagination/sort whenever it changes so it survives back navigation
    sessionStorage.setItem(
      tableStateKey,
      JSON.stringify({ order, orderBy, page, rowsPerPage })
    );
  }, [order, orderBy, page, rowsPerPage, tableStateKey]);

  const headCells = [
    { label: 'ClinVar Variant Name', id: 'variantName', numeric: false, disablePadding: false },
    { label: 'GRCh38 Variant Name', id: 'hgvsGrch38Name', numeric: false, disablePadding: false },
    { label: 'Chromosome', id: 'chrom', numeric: false, disablePadding: false },
    { label: 'GRCh38 Position', id: 'pos', numeric: false, disablePadding: false },
    { label: 'Ref Allele', id: 'refAllele', numeric: false, disablePadding: false },
    { label: 'Alt Allele', id: 'altAllele', numeric: false, disablePadding: false },
  ];

  if (onAddVariant) {
    headCells.push({ label: 'Action', id: 'action', numeric: false, disablePadding: false, type: 'noSort', align: 'right' });
  }

  if (!data || data.length === 0) {
    return (
      <TableContainer component={StyledPaper}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          {noResultsMessage}
        </Box>
      </TableContainer>
    );
  }

  return (
    <>
      <TableContainer component={StyledPaper}>
        <StyledTable
          size='small'
          aria-label="variant search results table"
          key={JSON.stringify(data)}
        >
          <MuiTableHeader
            headCells={headCells}
            order={order}
            orderBy={orderBy}
            onRequestSort={(event, property) => handleRequestSort(event, property, order, orderBy, setOrder, setOrderBy)}
          />
          <TableBody>
            {stableSort(data, getComparator(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => {
                const isAdded = Array.isArray(addedVariantNames) && addedVariantNames.includes(row.variantName);
                return (
                  <StyledTableRow key={row.variantName ?? row.variantObjectId}>
                    <StyledTableCell>{row.variantName}</StyledTableCell>
                    <StyledTableCell>
                      <RouterLink
                        to={`/variantdetails/${row.variantObjectId}`}
                        onClick={() => sessionStorage.setItem('exploreRestoreOnReturn', '1')}
                        state={{
                          variantId: row.variantObjectId,
                          variantName: row.hgvsGrch38Name,
                          breadCrumbs: breadCrumbs,
                        }}
                      >
                        {row.hgvsGrch38Name}
                      </RouterLink>
                    </StyledTableCell>
                    <StyledTableCell>{row.chrom}</StyledTableCell>
                    <StyledTableCell>{row.pos}</StyledTableCell>
                    <StyledTableCell>{row.refAllele}</StyledTableCell>
                    <StyledTableCell>{row.altAllele}</StyledTableCell>
                    {onAddVariant && (
                      <StyledTableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => onAddVariant(row)}
                            disabled={isAdded}
                          >
                            {isAdded ? 'Added' : 'Add Variant'}
                          </Button>
                          {isAdded && (
                            <Tooltip title="Remove variant">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  aria-label="remove variant"
                                  onClick={() => onRemoveVariant?.(row)}
                                >
                                  <RemoveCircleOutline fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </StyledTableCell>
                    )}
                  </StyledTableRow>
                );
              })}
          </TableBody>
        </StyledTable>
      </TableContainer>
      <MuiTablePagination
        rowsPerPage={rowsPerPage}
        page={page}
        count={data.length}
        onPageChange={(event, newPage) => handleChangePage(event, newPage, setPage)}
        onRowsPerPageChange={(event) => handleChangeRowsPerPage(event, setRowsPerPage, setPage)}
      />
    </>
  );
};

SearchVariantsResultsTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      variantObjectId: PropTypes.string,
      variantName: PropTypes.string.isRequired,
      hgvsGrch38Name: PropTypes.string,
      chrom: PropTypes.string,
      pos: PropTypes.string,
      refAllele: PropTypes.string,
      altAllele: PropTypes.string,
    })
  ).isRequired,
  breadCrumbs: PropTypes.array,
  noResultsMessage: PropTypes.string,
  onAddVariant: PropTypes.func,
  onRemoveVariant: PropTypes.func,
  addedVariantNames: PropTypes.array,
  tableStateKey: PropTypes.string,
};

SearchVariantsResultsTable.defaultProps = {
  noResultsMessage: 'No Search Results',
  tableStateKey: DEFAULT_TABLE_STATE_KEY,
};

export default SearchVariantsResultsTable;
